/**
 * Client-safe pre-launch registration gate.
 *
 * The browser only needs the public registration flag and stable error codes.
 * Authoritative invite list: public.private_beta_invitations (Auth Hook + app gates).
 * ADMIN_EMAIL_ALLOWLIST is emergency/local fallback only when DB lookup is unavailable.
 *
 * Launch checklist (private beta):
 *   - Supabase "Allow new users to sign up" = ON (invited users must be able to create accounts)
 *   - NEXT_PUBLIC_ALLOW_PUBLIC_REGISTRATION=false
 *   - Before User Created hook enabled → blocks uninvited signup
 *   - Seed private_beta_invitations before testing
 *
 * When public registration opens (after paid workspace gate is live):
 *   1. Deploy code with current_user_has_workspace_access + middleware paid gate
 *   2. Apply workspace/webhook migrations
 *   3. Set NEXT_PUBLIC_ALLOW_PUBLIC_REGISTRATION=true
 *   4. Set app_settings.enforce_invite_signup=false and/or disable the Auth Hook
 *   5. Redeploy
 *
 * Public registration alone must NEVER grant workspace access — only verified
 * paid Creator/Studio (or grandfathered active invitation) does.
 */

export const ALLOW_PUBLIC_REGISTRATION =
  process.env.NEXT_PUBLIC_ALLOW_PUBLIC_REGISTRATION === 'true';

/** Whether a new account may be created (sign-up) when only the public flag is considered. */
export function isRegistrationAllowed(): boolean {
  return ALLOW_PUBLIC_REGISTRATION;
}

export const REGISTRATION_DISABLED_MESSAGE =
  'AIMaker Studio is currently accepting users by invitation only. Join our waitlist to receive early access.';

export const ACCESS_RESTRICTED_MESSAGE =
  'AIMaker Studio is currently accepting users by invitation only. Join our waitlist to receive early access.';

/** Stable code thrown/returned so the UI can localize the invite rejection. */
export const INVITE_EMAIL_NOT_ALLOWED_CODE = 'INVITE_EMAIL_NOT_ALLOWED';

/**
 * English fallback for API responses / non-UI callers.
 * Prefer locale key auth.inviteEmailNotAllowed in the browser.
 */
export const INVITE_EMAIL_NOT_ALLOWED_MESSAGE =
  'This email is not on the private beta invitation list. Use your approved invitation email, or join the waitlist for access.';
